import API from './API';
import { fetchPaged, dayAfter } from './crud';

// -----------------------------------------------------------------------------
//  AGENDA — un'unica fonte per prenotazioni E spostamenti interni.
//
//  Un operatore non pensa «prenotazioni» e «spostamenti» separatamente: pensa
//  «cosa succede martedì». Qui le due fonti si leggono con la STESSA finestra
//  e si normalizzano nella stessa riga.
//
//  ⚠️ Lo scoping sul partner NON si fa qui: lo impone il server
//  (`@TenantCrudAuth` filtra su `partnerId` dal token). Aggiungere un filtro
//  client-side darebbe l'illusione che sia lui a proteggere i dati.
// -----------------------------------------------------------------------------

// I join servono a costruire la riga: senza i punti non c'è il percorso, senza
// le tratte non ci sono gli orari veri. Ripresi dal gestionale admin, dove
// questa lista è in esercizio.
const JOIN_BOOKING = [
  'pickup_bookings',
  'pickup_bookings.point',
  'pickup_bookings.driver_pickup',

  'delivery_bookings',
  'delivery_bookings.point_origin_delivery',
  'delivery_bookings.point_destination_delivery',
  'delivery_bookings.driver_delivery',

  'escort_bookings',
  'escort_bookings.point_origin_escort',
  'escort_bookings.point_destination_escort',
  'escort_bookings.driver_escort',

  'customer',
  'car',
  'headquarter',
];

const JOIN_JOURNEY = [
  'routes',
  'routes.startPoint',
  'routes.endPoint',
  'routes.driver',
  'routes.jolly_driver',
  // Chi rientra a bordo: serve a contare i posti liberi di una corsa.
  'routes.passengers',
];

/**
 * Prenotazioni della finestra [from, to], estremi INCLUSI come li intende chi
 * guarda un calendario.
 *
 * ⚠️ `data` è un datetime, quindi `data <= '2026-09-08'` significa
 * `<= 2026-09-08 00:00:00` e il giorno finale resterebbe fuori: si usa
 * `data < dayAfter(to)`.
 */
export function bookingsInWindow(from, to) {
  return fetchPaged('/booking', {
    filter: [
      'state||$ne||canceled',
      `data||$gte||${from}`,
      `data||$lt||${dayAfter(to)}`,
    ],
    join: JOIN_BOOKING,
    sort: 'data,ASC',
  });
}

/**
 * Spostamenti interni della stessa finestra.
 *
 * Le ELIMINATA restano fuori: sono corse annullate, non occupano la giornata.
 */
export function journeysInWindow(from, to) {
  return fetchPaged('/journey', {
    filter: [
      `date||$gte||${from}`,
      `date||$lt||${dayAfter(to)}`,
      'status||$ne||ELIMINATA',
    ],
    join: JOIN_JOURNEY,
    sort: 'date,ASC',
  });
}

/**
 * Le due letture insieme.
 *
 * `truncated` è vero se ANCHE UNA SOLA delle due è stata troncata: l'agenda
 * sarebbe parziale in entrambi i casi, e va detto invece di mostrare un elenco
 * più corto senza spiegazioni.
 */
export async function agendaInWindow(from, to) {
  const [b, j] = await Promise.all([
    bookingsInWindow(from, to),
    journeysInWindow(from, to),
  ]);
  return {
    bookings: b.rows,
    journeys: j.rows,
    truncated: b.truncated || j.truncated,
    total: b.total + j.total,
  };
}

/**
 * Una singola prenotazione, con tutto quello che serve al dettaglio.
 *
 * ⚠️ NON riusa `bookingAPI.getBookingById`: quello carica i `planners` ma non
 * la PERSONA assegnata, e il dettaglio deve mostrare chi arriva.
 *
 * ⚠️ TRAPPOLA: `planner.jolly` NON è il jolly. È il driver assegnato a quel
 * planner, chiunque sia; il ruolo lo dice `planner.type` ('driver' | 'jolly').
 * Il nome della relazione è storico e non si può cambiare senza rompere i
 * client. Vedi `appartenenza-corsa.ts` nel backend.
 */
export function bookingDetail(id) {
  return API.get(`/booking/${id}`, {
    params: {
      join: [
        'pickup_bookings',
        'pickup_bookings.point',
        'pickup_bookings.driver_pickup',
        'pickup_bookings.planners',
        'pickup_bookings.planners.jolly',

        'escort_bookings',
        'escort_bookings.point_origin_escort',
        'escort_bookings.point_destination_escort',
        'escort_bookings.driver_escort',
        'escort_bookings.planners',
        'escort_bookings.planners.jolly',

        'delivery_bookings',
        'delivery_bookings.point_origin_delivery',
        'delivery_bookings.point_destination_delivery',
        'delivery_bookings.driver_delivery',
        'delivery_bookings.planners',
        'delivery_bookings.planners.jolly',

        'customer',
        'car',
        'tasks',
        'headquarter',
      ],
    },
  });
}

/**
 * Allegati di una prenotazione.
 *
 * I file stanno sull'object storage: l'elenco restituisce link temporanei
 * (`url`). Si caricano in base64, come data URI, con il loro nome. Il backend
 * controlla che la prenotazione sia del partner.
 */
export function allegatiPrenotazione(id) {
  return API.get(`/booking/${id}/attachments`);
}

export function nuovoAllegato(id, { file, fileName }) {
  return API.post(
    `/booking/${id}/attachments`,
    { file, file_name: String(fileName || '').slice(0, 255) || undefined },
    { skipErrorToast: true },
  );
}

export function cancellaAllegato(id, allegatoId) {
  return API.delete(`/booking/${id}/attachments/${allegatoId}`, { skipErrorToast: true });
}

/**
 * Imponibile e totale della prenotazione con le tariffe del partner.
 *
 * Li calcola il backend al momento; sul booking restano salvati quelli Simba.
 * `complete: false` = manca una tariffa o una distanza: nessun totale.
 */
export function prezzoPartnerPrenotazione(id) {
  return API.get(`/booking/${id}/partner-price`, { skipErrorToast: true });
}

export default {
  bookingsInWindow, journeysInWindow, agendaInWindow, bookingDetail,
  allegatiPrenotazione, nuovoAllegato, cancellaAllegato, prezzoPartnerPrenotazione,
};
